import ContactVerificationNotice from "./ContactVerificationNotice";
import { formatAbn } from "utils/abn";
/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import {
  LuArchive,
  LuArrowLeft,
  LuBell,
  LuClock,
  LuCoins,
  LuDownload,
  LuPencil,
  LuTrash2,
  LuX,
} from "react-icons/lu";

import { useMaterialUIController } from "context";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import AppHeader from "components/AppHeader";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";
import PageSkeleton from "components/Skeleton/PageSkeleton";
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { ThemedSelect } from "components/FormControls";
import { loadingAction } from "ReduxReducers/loadingReducer";
import { formatCurrencyAUD } from "utils/formatCurrency";
import { createStatementAttachment } from "utils/statementPdf";
import InvoiceAttachmentPreview from "layouts/Invoices/InvoiceAttachmentPreview";
import { showResMsg, getScreenRight } from "utils/generic";
import {
  deleteContactRecords,
  getContactDetailsByID,
  updateContactData,
  getCustomerActivityStatements,
} from "utils/contactAPI";
import { getCutomerInvoiceStatement } from "utils/invoiceAPI";
import {
  filterContactActivities,
  groupContactActivities,
} from "./contactActivity";

import "./contactDetails.css";
import "layouts/Quotation/quotation.css";

const SCREEN_NAME = "Contacts";

export default function ContactDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // App shell state
  const [navOpen, setNavOpen] = useState(false);
  const loginDetails = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("LOGIN_DETAILS")) || {};
    } catch {
      return {};
    }
  }, []);
  const organisation =
    loginDetails?.companyName || loginDetails?.name || "Organisation";
  const role = loginDetails?.userRole || "Owner";
  const planName = loginDetails?.planName || "Silver";
  const userName = loginDetails?.name || organisation;
  const profileImage = loginDetails?.profileImage || "";
  const businessUserID = loginDetails?.businessUserID || loginDetails?._id;

  // Contact details state
  const [contact, setContact] = useState(null);
  const [contactLoading, setContactLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview"); // "overview" | "invoices" | "activity"
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  // Invoices tab state
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // Activity tab state
  const [activityFilter, setActivityFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  // Modals & toast state
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementPreview, setStatementPreview] = useState(null);

  // Load Contact Info
  const loadContact = useCallback(() => {
    if (!id) return;
    setContactLoading(true);
    dispatch(loadingAction(true));
    getContactDetailsByID(
      { _id: id, businessUserID },
      (res) => {
        setContactLoading(false);
        dispatch(loadingAction(false));
        if (
          res?.Result_Status &&
          Array.isArray(res?.Result_Output) &&
          res.Result_Output.length > 0
        ) {
          setContact(res.Result_Output[0]);
        }
      },
      dispatch,
      () => setContactLoading(false)
    );
  }, [id, businessUserID, dispatch]);

  // Load Invoices for this Contact
  const loadInvoices = useCallback(() => {
    if (!id || !businessUserID) return;
    setInvoicesLoading(true);
    getCutomerInvoiceStatement(
      {
        _id: id,
        businessUserID,
        page: 1,
        limit: 50,
        status: "All",
        searchParameterList: ["invoiceNumber", "purchaseOrderNumber", "status"],
      },
      (res) => {
        setInvoicesLoading(false);
        if (res?.Result_Status && res?.Result_Output?.docs) {
          setInvoices(res.Result_Output.docs);
        } else if (Array.isArray(res?.Result_Output)) {
          setInvoices(res.Result_Output);
        }
      },
      dispatch
    ).finally(() => setInvoicesLoading(false));
  }, [id, businessUserID, dispatch]);

  useEffect(() => {
    loadContact();
    loadInvoices();
  }, [loadContact, loadInvoices]);

  // Close menus on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest(".contact-details-actions")) {
        setMoreMenuOpen(false);
      }
    };
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, []);

  // Computed financial stats
  const totalInvoiced = useMemo(() => {
    if (invoices.length > 0) {
      return invoices.reduce(
        (acc, curr) => acc + Number(curr.totalAmount || curr.invoiceTotal || 0),
        0
      );
    }
    return Number(contact?.totalInvoiced || 0);
  }, [invoices, contact]);

  const totalPaid = useMemo(() => {
    if (invoices.length > 0) {
      return invoices.reduce((acc, curr) => {
        const total = Number(curr.totalAmount || curr.invoiceTotal || 0);
        const due = Number(curr.dueAmount || curr.outstandingBalance || 0);
        return acc + Math.max(0, total - due);
      }, 0);
    }
    return Number(contact?.totalPaid || 0);
  }, [invoices, contact]);

  const openBalance = useMemo(() => {
    if (invoices.length > 0) {
      return invoices.reduce(
        (acc, curr) =>
          acc + Number(curr.dueAmount || curr.outstandingBalance || 0),
        0
      );
    }
    return Number(contact?.theyOwe || contact?.openBalance || 0);
  }, [invoices, contact]);

  const paidInvoiceCount = useMemo(() => {
    return invoices.filter(
      (inv) =>
        (inv.status || "").toLowerCase() === "paid" ||
        Number(inv.dueAmount || 0) === 0
    ).length;
  }, [invoices]);

  const overdueInvoiceCount = useMemo(() => {
    return invoices.filter(
      (inv) => (inv.status || "").toLowerCase() === "overdue"
    ).length;
  }, [invoices]);

  // Invoice reminder status
  const isReminderOn = useMemo(() => {
    if (!contact) return false;
    const val = contact.invoiceReminder || contact.autoInvoiceReminderFlag;
    return val === true || val === "Yes" || val === "true" || val === "Enabled";
  }, [contact]);

  const companyName =
    contact?.companyName || contact?.name || "Contact Details";
  const contactType = contact?.contactType || "Customer";
  const status = contact?.contactStatus || "Active";

  const contactActivities = useMemo(
    () => [
      {
        id: "contact-created",
        type: "created",
        daysAgo: 0,
        title: "Contact created",
        description: `${companyName} added · 9:12 AM`,
      },
      {
        id: "billing-updated",
        type: "billing",
        daysAgo: 0,
        title: "Billing details updated",
        description: `Payment terms set to ${
          contact?.paymentTerms || "7 days"
        } · 9:14 AM`,
      },
      {
        id: "reminders-updated",
        type: "reminders",
        daysAgo: 0,
        title: `Invoice reminders ${isReminderOn ? "enabled" : "disabled"}`,
        description: `Automatic reminders ${
          isReminderOn ? "turned on" : "turned off"
        } · 9:16 AM`,
      },
      {
        id: "contact-updated",
        type: "billing",
        daysAgo: 1,
        title: "Contact details updated",
        description: `Primary contact confirmed as ${
          contact?.name || "Jordan Lee"
        } · Yesterday, 4:32 PM`,
      },
    ],
    [companyName, contact?.name, contact?.paymentTerms, isReminderOn]
  );

  const filteredActivities = useMemo(
    () =>
      filterContactActivities(contactActivities, activityFilter, dateFilter),
    [contactActivities, activityFilter, dateFilter]
  );
  const activitySections = useMemo(
    () => groupContactActivities(filteredActivities),
    [filteredActivities]
  );

  if (contactLoading || invoicesLoading) {
    return (
      <div
        className={`contact-details-page business-dashboard${
          darkMode ? " dark" : ""
        }`}
        data-theme={darkMode ? "dark" : "light"}
      >
        <DashboardNavigation
          open={navOpen}
          onClose={() => setNavOpen(false)}
          organisation={organisation}
          role={role}
          planName={planName}
        />
        <div className="dashboard-main">
          <AppHeader
            onOpenNav={() => setNavOpen(true)}
            userName={userName}
            profileImage={profileImage}
          />
          <main className="contact-details-content">
            <PageSkeleton
              type="detail"
              variant="contact"
              label="Loading contact details…"
            />
          </main>
        </div>
      </div>
    );
  }

  // Handlers
  const handleArchive = async () => {
    if (!contact || archiveLoading) return;
    setArchiveLoading(true);
    dispatch(loadingAction(true));
    const newStatus =
      contact.contactStatus === "Archived" ? "Active" : "Archived";
    const payload = { ...contact, contactStatus: newStatus };
    if (!payload.countryId || !String(payload.countryId).trim())
      delete payload.countryId;
    if (!payload.stateId || !String(payload.stateId).trim())
      delete payload.stateId;
    updateContactData(
      payload,
      (res) => {
        dispatch(loadingAction(false));
        setArchiveLoading(false);
        setArchiveModalOpen(false);
        if (res?.Result_Status) {
          showDraftSavedMessage(
            newStatus === "Archived"
              ? "Contact archived successfully"
              : "Contact unarchived successfully"
          );
          loadContact();
        } else {
          showResMsg(
            res?.Result_Message || "Failed to update contact status",
            2000,
            false
          );
        }
      },
      dispatch
    );
  };

  const handleDelete = async () => {
    if (!contact) return;
    dispatch(loadingAction(true));
    deleteContactRecords(
      { _id: contact._id, businessUserID },
      (res) => {
        dispatch(loadingAction(false));
        setDeleteModalOpen(false);
        if (res?.Result_Status) {
          showResMsg("Contact deleted successfully", 2000, true);
          navigate("/contacts");
        } else {
          showResMsg(
            res?.Result_Message || "Failed to delete contact",
            2000,
            false
          );
        }
      },
      dispatch
    );
  };

  const handleDownloadStatement = async () => {
    setMoreMenuOpen(false);
    if (!contact?._id || statementLoading) return;
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const to = now.toISOString();
    setStatementLoading(true);
    try {
      await getCustomerActivityStatements(
        {
          businessUserID,
          contactIDs: [contact._id],
          from,
          to,
          type: "Print",
        },
        (res) => {
          if (res?.Result_Status) {
            setStatementPreview(createStatementAttachment(res.Result_Output));
          } else {
            showResMsg(
              res?.Result_Message || "Unable to generate the statement PDF. Please try again.",
              2000,
              false
            );
          }
        },
        dispatch
      );
    } catch (error) {
      showResMsg(error.message || "Unable to generate the statement PDF. Please try again.", 2000, false);
    } finally {
      setStatementLoading(false);
    }
  };

  return (
    <div
      className={`contact-details-page business-dashboard${
        darkMode ? " dark" : ""
      }`}
      data-theme={darkMode ? "dark" : "light"}
    >
      {/* Sidebar Navigation */}
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        organisation={organisation}
        role={role}
        planName={planName}
      />

      <div className="dashboard-main">
        {/* Top Header */}
        <AppHeader
          onOpenNav={() => setNavOpen(true)}
          userName={userName}
          profileImage={profileImage}
        />

        <main className="contact-details-content">
          {/* Header section with back button, titles, badges, and actions */}
          <div className="contact-details-header">
            <Link to="/contacts" className="contact-details-back-btn">
              <LuArrowLeft />
              <span>Back to Contacts</span>
            </Link>

            <div className="contact-details-title-row">
              <div className="contact-details-identity">
                <h1 className="contact-details-title">{companyName}</h1>
                <div className="contact-details-badges">
                  <span className="contact-badge-pill">{contactType}</span>
                  <span
                    className={`contact-badge-pill ${
                      status === "Archived" ? "status-archived" : ""
                    }`}
                  >
                    {status === "Archived" && (
                      <span className="contact-badge-dot" />
                    )}
                    {status}
                  </span>
                </div>
              </div>

              <div className="contact-details-actions">
                <button
                  type="button"
                  className="contact-btn-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMoreMenuOpen((prev) => !prev);
                  }}
                >
                  <span>More</span>
                </button>

                <Link
                  to={`/contacts/editContact/${id}`}
                  className="contact-btn-primary"
                >
                  <LuPencil />
                  <span>Edit contact</span>
                </Link>

                {/* More Dropdown Menu */}
                {moreMenuOpen && (
                  <div className="contact-dropdown-menu">
                    <button
                      type="button"
                      className="contact-dropdown-item"
                      onClick={() => {
                        setMoreMenuOpen(false);
                        setArchiveModalOpen(true);
                      }}
                    >
                      <LuArchive />
                      <span>
                        {status === "Archived"
                          ? "Unarchive contact"
                          : "Archive contact"}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="contact-dropdown-item"
                      onClick={handleDownloadStatement}
                      disabled={statementLoading}
                    >
                      <LuDownload />
                      <span>Activity statement</span>
                    </button>
                    <button
                      type="button"
                      className="contact-dropdown-item danger"
                      onClick={() => {
                        setMoreMenuOpen(false);
                        setDeleteModalOpen(true);
                      }}
                    >
                      <LuTrash2 />
                      <span>Delete contact</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Details Tabs (Overview, Invoices, Activity) */}
          <ContactVerificationNotice contact={contact} onSent={loadContact} />
          <nav className="contact-details-tabs" aria-label="Contact views">
            <button
              type="button"
              className={`contact-tab-btn ${
                activeTab === "overview" ? "active" : ""
              }`}
              onClick={() => setActiveTab("overview")}
            >
              Overview
            </button>
            <button
              type="button"
              className={`contact-tab-btn ${
                activeTab === "invoices" ? "active" : ""
              }`}
              onClick={() => setActiveTab("invoices")}
            >
              Invoices
            </button>
            <button
              type="button"
              className={`contact-tab-btn ${
                activeTab === "activity" ? "active" : ""
              }`}
              onClick={() => setActiveTab("activity")}
            >
              Activity
            </button>
          </nav>

          {/* TAB 1: OVERVIEW */}
          {activeTab === "overview" && (
            <>
              {/* Financial summary (3 stat cards) */}
              <div className="contact-stat-grid">
                <div className="contact-stat-card">
                  <div className="contact-stat-header">
                    <h2 className="contact-stat-title">Open balance</h2>
                    <span className="contact-stat-icon">
                      <LuCoins />
                    </span>
                  </div>
                  <div className="contact-stat-headline">
                    <p className="contact-stat-amount">
                      {formatCurrencyAUD(openBalance)}
                    </p>
                    <p className="contact-stat-subtitle">Outstanding amount</p>
                  </div>
                </div>

                <div className="contact-stat-card">
                  <div className="contact-stat-header">
                    <h2 className="contact-stat-title">Payment terms</h2>
                    <span className="contact-stat-icon">
                      <LuClock />
                    </span>
                  </div>
                  <div className="contact-stat-headline">
                    <p className="contact-stat-amount">
                      {contact?.paymentTerms || "7 days"}
                    </p>
                    <p className="contact-stat-subtitle">
                      Default for new invoices
                    </p>
                  </div>
                </div>

                <div className="contact-stat-card">
                  <div className="contact-stat-header">
                    <h2 className="contact-stat-title">Invoice reminders</h2>
                    <span className="contact-stat-icon">
                      <LuBell />
                    </span>
                  </div>
                  <div className="contact-stat-headline">
                    <p className="contact-stat-amount">
                      {isReminderOn ? "On" : "Off"}
                    </p>
                    <p className="contact-stat-subtitle">
                      {isReminderOn ? "Sent automatically" : "Disabled"}
                    </p>
                  </div>
                </div>
              </div>

              {/* 2-Column Overview Body */}
              <div className="contact-overview-body">
                {/* Left Column: Business, Contact Person, Billing, Address */}
                <div className="contact-info-col">
                  {/* Card 1: Business details */}
                  <div className="contact-detail-card">
                    <h3 className="contact-detail-card-title">
                      Business details
                    </h3>
                    <div className="contact-detail-list">
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">ABN</span>
                        <span className="contact-detail-val">
                          {formatAbn(contact?.abnNumber) || "Not provided"}
                        </span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">
                          Contact type
                        </span>
                        <span className="contact-detail-val">
                          {contactType}
                        </span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">
                          Contact status
                        </span>
                        <span className="contact-detail-val">{status}</span>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Primary contact */}
                  <div className="contact-detail-card">
                    <h3 className="contact-detail-card-title">
                      Primary contact
                    </h3>
                    <div className="contact-detail-list">
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">Name</span>
                        <span className="contact-detail-val">
                          {contact?.name || "Not provided"}
                        </span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">Email</span>
                        <span className="contact-detail-val">
                          {contact?.emailAddress || "Not provided"}
                        </span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">Mobile</span>
                        <span className="contact-detail-val">
                          {contact?.phoneNumber || "Not provided"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Billing preferences */}
                  <div className="contact-detail-card">
                    <h3 className="contact-detail-card-title">
                      Billing preferences
                    </h3>
                    <div className="contact-detail-list">
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">
                          Payment terms
                        </span>
                        <span className="contact-detail-val">
                          {contact?.paymentTerms || "7 days"}
                        </span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">
                          Invoice reminders
                        </span>
                        <span className="contact-detail-val">
                          {isReminderOn ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">Currency</span>
                        <span className="contact-detail-val">AUD</span>
                      </div>
                    </div>
                  </div>

                  {/* Card 4: Billing address */}
                  <div className="contact-detail-card">
                    <h3 className="contact-detail-card-title">
                      Billing address
                    </h3>
                    <div className="contact-detail-list">
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">Street</span>
                        <span className="contact-detail-val">
                          {contact?.streetAddress ||
                            contact?.address ||
                            "Not provided"}
                        </span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">
                          Suburb / State
                        </span>
                        <span className="contact-detail-val">
                          {contact?.city || contact?.state
                            ? `${contact?.city || ""}${
                                contact?.city && contact?.state ? " / " : ""
                              }${contact?.state || ""}`
                            : "Not provided"}
                        </span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">Country</span>
                        <span className="contact-detail-val">
                          {contact?.country || "Australia"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Notes & Recent activity */}
                <div className="contact-side-col">
                  {/* Notes Card */}
                  <div className="contact-detail-card">
                    <h3 className="contact-detail-card-title">Notes</h3>
                    <div className="contact-detail-list">
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">
                          Latest note
                        </span>
                        <span
                          className="contact-detail-val"
                          style={{
                            maxWidth: 220,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {contact?.notes || "No notes added"}
                        </span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="contact-detail-label">Visibility</span>
                        <span className="contact-detail-val">Team only</span>
                      </div>
                    </div>
                  </div>

                  {/* Recent Activity Card */}
                  <div className="contact-detail-card">
                    <h3 className="contact-detail-card-title">
                      Recent activity
                    </h3>
                    <div className="contact-timeline">
                      <div className="contact-timeline-item">
                        <div className="contact-timeline-indicator">
                          <LuClock />
                        </div>
                        <div className="contact-timeline-text">
                          <p className="contact-timeline-headline">
                            Contact created
                          </p>
                          <p className="contact-timeline-sub">
                            Created in {organisation}
                          </p>
                        </div>
                      </div>
                      <div className="contact-timeline-connector" />

                      <div className="contact-timeline-item">
                        <div className="contact-timeline-indicator">
                          <LuClock />
                        </div>
                        <div className="contact-timeline-text">
                          <p className="contact-timeline-headline">
                            Billing updated
                          </p>
                          <p className="contact-timeline-sub">
                            Payment terms set to{" "}
                            {contact?.paymentTerms || "7 days"}
                          </p>
                        </div>
                      </div>
                      <div className="contact-timeline-connector" />

                      <div className="contact-timeline-item">
                        <div className="contact-timeline-indicator">
                          <LuClock />
                        </div>
                        <div className="contact-timeline-text">
                          <p className="contact-timeline-headline">
                            Reminders {isReminderOn ? "enabled" : "disabled"}
                          </p>
                          <p className="contact-timeline-sub">
                            Automatic reminders{" "}
                            {isReminderOn ? "turned on" : "turned off"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* TAB 2: INVOICES */}
          {activeTab === "invoices" && (
            <>
              {/* Financial summary stat cards */}
              <div className="contact-stat-grid">
                <div className="contact-stat-card">
                  <div className="contact-stat-header">
                    <h2 className="contact-stat-title">Total invoiced</h2>
                    <span
                      className="contact-stat-icon contact-stat-icon--figma contact-stat-icon--total-invoiced"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="contact-stat-headline">
                    <p className="contact-stat-amount">
                      {formatCurrencyAUD(totalInvoiced)}
                    </p>
                    <p className="contact-stat-subtitle">
                      {invoices.length} invoices
                    </p>
                  </div>
                </div>

                <div className="contact-stat-card">
                  <div className="contact-stat-header">
                    <h2 className="contact-stat-title">Paid</h2>
                    <span
                      className="contact-stat-icon contact-stat-icon--figma contact-stat-icon--paid"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="contact-stat-headline">
                    <p className="contact-stat-amount">
                      {formatCurrencyAUD(totalPaid)}
                    </p>
                    <p className="contact-stat-subtitle">
                      {paidInvoiceCount}{" "}
                      {paidInvoiceCount === 1 ? "invoice" : "invoices"} paid
                    </p>
                  </div>
                </div>

                <div className="contact-stat-card">
                  <div className="contact-stat-header">
                    <h2 className="contact-stat-title">Outstanding</h2>
                    <span
                      className="contact-stat-icon contact-stat-icon--figma contact-stat-icon--outstanding"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="contact-stat-headline">
                    <p className="contact-stat-amount">
                      {formatCurrencyAUD(openBalance)}
                    </p>
                    <p className="contact-stat-subtitle">
                      {overdueInvoiceCount} overdue{" "}
                      {overdueInvoiceCount === 1 ? "invoice" : "invoices"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Invoices Register Table */}
              <div className="contact-invoices-card">
                <div className="contact-invoices-header">
                  <span className="contact-invoices-count">
                    {invoices.length} invoices
                  </span>
                  <span className="contact-invoices-meta">
                    {companyName} · Sorted by due date
                  </span>
                </div>

                {invoicesLoading ? (
                  <div
                    style={{
                      padding: 32,
                      textAlign: "center",
                      color: "var(--text-secondary)",
                    }}
                  >
                    Loading invoices...
                  </div>
                ) : invoices.length === 0 ? (
                  <div
                    style={{
                      padding: 48,
                      textAlign: "center",
                      color: "var(--text-secondary)",
                    }}
                  >
                    No invoices recorded for this contact yet.
                  </div>
                ) : (
                  <table className="contact-invoices-table">
                    <thead>
                      <tr>
                        <th className="contact-invoices-th">Invoice number</th>
                        <th className="contact-invoices-th">Customer</th>
                        <th className="contact-invoices-th">Invoice date</th>
                        <th className="contact-invoices-th">Due date</th>
                        <th className="contact-invoices-th">Payment status</th>
                        <th className="contact-invoices-th text-right">
                          Invoice total
                        </th>
                        <th className="contact-invoices-th text-right">
                          Outstanding balance
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => {
                        const invNumber =
                          inv.invoiceNumber || `INV-${inv._id?.slice(-4)}`;
                        const invStatus = (
                          inv.status || "Pending"
                        ).toLowerCase();
                        const isOverdue = invStatus === "overdue";
                        const isPaid = invStatus === "paid";
                        const total = Number(
                          inv.totalAmount || inv.invoiceTotal || 0
                        );
                        const due = Number(
                          inv.dueAmount || inv.outstandingBalance || 0
                        );

                        return (
                          <tr
                            key={inv._id}
                            className="contact-invoices-tr"
                            onClick={(e) => {
                              if (!e.target.closest("a, button, input")) {
                                navigate(`/invoices/view/${inv._id}`);
                              }
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <td className="contact-invoices-td">
                              <Link
                                to={`/invoices/view/${inv._id}`}
                                className="contact-inv-number"
                              >
                                {invNumber}
                              </Link>
                            </td>
                            <td className="contact-invoices-td">
                              {companyName}
                            </td>
                            <td className="contact-invoices-td">
                              {inv.invoiceDate || inv.date || "—"}
                            </td>
                            <td className="contact-invoices-td">
                              {inv.dueDate || inv.due || "—"}
                            </td>
                            <td className="contact-invoices-td">
                              <span
                                className={`inv-status-pill ${
                                  isPaid
                                    ? "status-paid"
                                    : isOverdue
                                    ? "status-overdue"
                                    : "status-pending"
                                }`}
                              >
                                {inv.status || "Pending"}
                              </span>
                            </td>
                            <td
                              className="contact-invoices-td text-right"
                              style={{ fontWeight: 500 }}
                            >
                              {formatCurrencyAUD(total)}
                            </td>
                            <td
                              className="contact-invoices-td text-right"
                              style={{ fontWeight: 500 }}
                            >
                              {formatCurrencyAUD(due)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* TAB 3: ACTIVITY */}
          {activeTab === "activity" && (
            <>
              <div className="contact-activity-toolbar">
                <div>
                  <h2 className="contact-activity-title">Activity history</h2>
                  <p className="contact-activity-sub">
                    {activityFilter === "all" && dateFilter === "all"
                      ? "Showing all activity"
                      : `Showing ${filteredActivities.length} matching ${
                          filteredActivities.length === 1
                            ? "activity"
                            : "activities"
                        }`}{" "}
                    for {companyName}
                  </p>
                </div>

                <div className="contact-activity-filters">
                  <ThemedSelect
                    className="contact-activity-filter contact-activity-filter--type"
                    dark={darkMode}
                    value={activityFilter}
                    onChange={(e) => setActivityFilter(e.target.value)}
                    aria-label="Filter activity by type"
                  >
                    <option value="all">All activity</option>
                    <option value="created">Contact created</option>
                    <option value="billing">Billing updates</option>
                    <option value="reminders">Reminders</option>
                  </ThemedSelect>

                  <ThemedSelect
                    className="contact-activity-filter contact-activity-filter--date"
                    dark={darkMode}
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    aria-label="Filter activity by date"
                  >
                    <option value="all">All dates</option>
                    <option value="today">Today</option>
                    <option value="week">Past 7 days</option>
                    <option value="month">Past 30 days</option>
                  </ThemedSelect>
                </div>
              </div>

              <div className="contact-activity-card">
                {activitySections.length ? (
                  activitySections.map((section, sectionIndex) => (
                    <section
                      className="contact-activity-section"
                      key={section.key}
                    >
                      <h3 className="contact-activity-section-title">
                        {section.label}
                      </h3>
                      <div className="contact-timeline">
                        {section.activities.map((activity, index) => (
                          <React.Fragment key={activity.id}>
                            <div className="contact-timeline-item">
                              <div className="contact-timeline-indicator">
                                <LuClock />
                              </div>
                              <div className="contact-timeline-text">
                                <p className="contact-timeline-headline">
                                  {activity.title}
                                </p>
                                <p className="contact-timeline-sub">
                                  {activity.description}
                                </p>
                              </div>
                            </div>
                            {index < section.activities.length - 1 && (
                              <div className="contact-timeline-connector" />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                      {sectionIndex < activitySections.length - 1 && (
                        <div className="contact-activity-section-gap" />
                      )}
                    </section>
                  ))
                ) : (
                  <div className="contact-activity-empty" role="status">
                    No activity matches these filters.
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* MODAL 1: ARCHIVE CONTACT (Figma 1591:150629) */}
      {archiveModalOpen && (
        <div
          className="contact-modal-overlay"
          onClick={() => !archiveLoading && setArchiveModalOpen(false)}
        >
          <div
            className="contact-modal-dialog"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-busy={archiveLoading}
          >
            <div className="contact-modal-header">
              <div className="contact-modal-title-wrap">
                <span className="contact-modal-icon">
                  <LuArchive />
                </span>
                <h3 className="contact-modal-title">
                  {status === "Archived"
                    ? "Unarchive contact"
                    : "Archive contact"}
                </h3>
              </div>
              <button
                type="button"
                className="contact-modal-close"
                disabled={archiveLoading}
                onClick={() => setArchiveModalOpen(false)}
                aria-label="Close dialog"
              >
                <LuX />
              </button>
            </div>
            <div className="contact-modal-divider" />
            <div className="contact-modal-body">
              <p>
                {status === "Archived"
                  ? `Unarchive ${companyName}? You will be able to create new invoices and quotations for this contact again.`
                  : `Archive ${companyName}? Existing invoices stay accessible, but new invoices can’t be created.`}
              </p>
            </div>
            <div className="contact-modal-footer">
              <button
                type="button"
                className="contact-modal-btn-cancel"
                disabled={archiveLoading}
                onClick={() => setArchiveModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="contact-modal-btn-confirm"
                onClick={handleArchive}
                disabled={archiveLoading}
                aria-busy={archiveLoading}
              >
                {archiveLoading && <CircularProgress size={16} color="inherit" aria-hidden="true" />}
                {archiveLoading
                  ? status === "Archived" ? "Unarchiving…" : "Archiving…"
                  : status === "Archived"
                  ? "Unarchive contact"
                  : "Archive contact"}
              </button>
            </div>
          </div>
        </div>
      )}

      <InvoiceAttachmentPreview
        attachment={statementPreview}
        onClose={() => setStatementPreview(null)}
        quotationStyle
      />
      <DeleteConfirmationDialog
        className="contact-delete-confirmation"
        open={deleteModalOpen}
        title="Delete contact"
        message={`Are you sure you want to delete ${companyName}? This action cannot be undone and will permanently remove this contact.`}
        confirmLabel="Delete contact"
        handleDelete={handleDelete}
        handleCancel={() => setDeleteModalOpen(false)}
        closeModalClick={() => setDeleteModalOpen(false)}
      />
    </div>
  );
}
